import { useEffect, useRef, useState } from 'react'
import { CheckCircle2, AlertCircle, X } from 'lucide-react'
import { useAuth } from '@/auth/useAuth'
import { useAuthStore } from '@/auth/authStore'
import { useAcceptInvite } from '@/hooks/useHousehold'
import { clearPendingInvite, getPendingInvite } from '@/lib/householdInvite'
import { softNavigate } from '@/lib/softRouting'

/**
 * v0.8.51 — Redeems a stored household invite once the user is signed in.
 *
 * Mounted once at the app root so it works however sign-in completed:
 * OTP code on `/join`, magic link via `/auth/callback` (possibly in a new
 * tab), or an already-signed-in user opening the link.
 *
 * Waits for the initial workspace resolution to settle first — otherwise
 * that in-flight resolve could land AFTER ours and pin the user to their
 * Personal workspace. The token is cleared whatever the outcome so a bad
 * link can't retry forever; the result shows as a dismissible toast.
 */
export function PendingInviteHandler() {
  const { isAuthenticated, isLoading } = useAuth()
  const resolveAttempted = useAuthStore((s) => s.workspaceResolveAttempted)
  const acceptInvite = useAcceptInvite()
  const inFlight = useRef(false)
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null)

  useEffect(() => {
    if (isLoading || !isAuthenticated || !resolveAttempted || inFlight.current) return
    const token = getPendingInvite()
    if (!token) return
    inFlight.current = true
    acceptInvite(token)
      .then(() =>
        setResult({
          ok: true,
          message: 'You joined the household. Shared debts and the monthly plan are now here.',
        })
      )
      .catch((err: unknown) =>
        setResult({ ok: false, message: err instanceof Error ? err.message : String(err) })
      )
      .finally(() => {
        clearPendingInvite()
        inFlight.current = false
        if (window.location.pathname.startsWith('/join')) softNavigate('/')
      })
  }, [isLoading, isAuthenticated, resolveAttempted, acceptInvite])

  if (!result) return null

  return (
    <div
      role="status"
      className={`fixed left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-[110] flex gap-2 items-start rounded-lg border p-3 text-sm shadow-xl bottom-[calc(5rem+env(safe-area-inset-bottom))] sm:bottom-4 ${
        result.ok
          ? 'bg-emerald-950 border-emerald-500/40 text-emerald-100'
          : 'bg-red-950 border-red-500/40 text-red-100'
      }`}
    >
      {result.ok ? (
        <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-emerald-400" />
      ) : (
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-400" />
      )}
      <span className="flex-1">
        {result.ok ? result.message : `Couldn’t join the household: ${result.message}`}
      </span>
      <button
        onClick={() => setResult(null)}
        aria-label="Dismiss"
        className="opacity-70 hover:opacity-100"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}
